import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Avatar, Tooltip} from 'antd';
import {StyledAllTabInput, StyledAllTabCompanyTagSelect} from './StyledAllTab';
import TeamsTab from '../TeamsTab';
import AvatarSelect from '../../../../Select/AvatarSelect';
import {tagFamilyConstants} from '../../../../../constants';
import {formHelpers, teamsHelpers} from '../../../../../utils/helpers';

const fieldProps = { onClick: (e) => e.stopPropagation() };

const AllTab = ({
  dispatch,
  employees,
  isEditorSupport,
  editorEmail,
  onActionClick,
  onUpdate,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'teams']);

  const employeeOptions = useMemo(() => formHelpers.getEmployeeOptions({employees, t}), [employees, t]);

  const handleUpdate = ({employee, propName, value}) => onUpdate?.({ data: { [propName]: value }, employee });

  const getInput = ({ defaultValue, onChange, propName, placeholderPropName, record, ...rest }) => {
    if (isEditorSupport) return defaultValue;
    return (
      <StyledAllTabInput
        defaultValue={defaultValue}
        debounceTime={2000}
        onDebouncedChange={(value) => handleUpdate?.({employee: record, propName, value})}
        placeholder={t?.('placeholderTemplates.enter', {name: t?.(placeholderPropName || propName.replaceAll('_', ' ')).toLowerCase()})}
        variant='borderless'
        {...fieldProps}
        {...rest}
      />
    );
  }

  const getReportToSelector = ({ defaultValue, propName, record }) => {
    const fullName = employees?.find(e => e.email === defaultValue)?.full_name;
    if (isEditorSupport) {
      return <Tooltip title={fullName} placement='top'>{defaultValue}</Tooltip>;
    }
    return (
      <Tooltip title={fullName} placement='top'>
        <AvatarSelect
          {...fieldProps}
          defaultValue={defaultValue}
          styles={{ popup: { root: { minWidth: 250, width: 'max-content' } } }}
          filteredKey='optionLabel'
          onChange={(value) => handleUpdate({employee: record, propName, value})}
          options={employeeOptions}
          placeholder={<Avatar />}
          size='large'
          suffixIcon={null}
        />
      </Tooltip>
    );
  }

  const getTagsSelector = ({ defaultValue, propName, record }) => {
    if (isEditorSupport) return defaultValue;
    return (
      <StyledAllTabCompanyTagSelect
        {...fieldProps}
        inputProps={{
          ...fieldProps,
          placeholder: t('placeholderTemplates.select', {name: t('team').toLowerCase()}),
          size: 'large'
        }}
        onChange={(value) => handleUpdate({employee: record, propName, value: value?.[0] || undefined})}
        optionValueName='id'
        tagFamily={tagFamilyConstants.TEAM}
        variant='single-select'
        selected={defaultValue ? [defaultValue] : []}
      />
    );
  }

  const columns = [
    {
      dataIndex: 'name',
      defaultSortOrder: 'descend',
      title: t('name'),
      sorter: true,
      render: (_, record) => teamsHelpers.getEmployeeDetails(record)
    },
    {
      dataIndex: 'type',
      title: t('type'),
      render: (_, record) => teamsHelpers.getUserGroupLabel(record, t)
    },
    {
      dataIndex: 'role',
      title: t('role'),
      render: (_, employee) => teamsHelpers.getRole({employee, onChange: (value) => handleUpdate({employee, propName: 'role', value})})
    },
    {
      dataIndex: 'job_title',
      title: t('jobTitle'),
      render: (defaultValue, record) => getInput({defaultValue, placeholder: `${t('product')} ${t('marketing')}`, propName: 'job_title', record})
    },
    {
      dataIndex: 'team_id',
      title: t('team'),
      render: (defaultValue, record) => getTagsSelector({defaultValue, propName: 'team_id', record})
    },
    {
      dataIndex: 'report_to',
      title: t('reportsTo'),
      render: (defaultValue, record) => getReportToSelector({defaultValue, propName: 'report_to', record})
    },
    {
      dataIndex: 'external_id',
      title: t('externalId'),
      render: (defaultValue, record) => getInput({defaultValue, placeholderPropName: 'value', propName: 'external_id', record})
    },
    {
      align: 'right',
      dataIndex: 'action',
      title: '',
      render: (_, record) => teamsHelpers.getActions({
        isEditorSupport,
        editorEmail,
        employee: record,
        t,
        onClick: onActionClick
      })
    }
  ];

  return (
    <TeamsTab
      {...rest}
      columns={columns}
    />
  );
}

AllTab.propTypes = {
  isEditorSupport: PropTypes.bool,
  editorEmail: PropTypes.string,
  onActionClick: PropTypes.func,
  onUpdate: PropTypes.func
}

const mapStateToProps = ({company: {employees}}) => ({
  employees,
});

export default connect(mapStateToProps, null)(AllTab);
